import { useEffect, useState, useMemo } from 'react';

import StaticdataApi from '@lcsc-ui/apis/staticdata';
import LcEdaCanvas from '@lcsc-ui/components/LcEdaCanvas';
import { LayoutProvider } from '@lcsc-ui/layouts/baseLayout/provider';
import useUserStore from '@lcsc-ui/store/user';
import ecpCurl, { setContext, asyncAll } from '@lcsc-ui/utils/ecpCurl';
import sendAxios from '@lcsc-ui/utils/request';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';

import API from '@/apis/item';
import Image from '@/components/Image';
import NavList from '@/components/navList';
import { defaultImg } from '@/constants';
import { useProductStockPriceEffect } from '@/hooks/useProductStockPriceEffect';

const DynamicItemRightPage = dynamic(() => import('@/components/detailPage/itemRightPage'));
const DynamicItemRightInquiry = dynamic(() => import('@/components/detailPage/itemRightInquiry'));

export default function ProductViewJpg({ webData, price, resourceData, pageUrl }) {
  const router = useRouter();
  const { updateProjectConfigVO, updateFromZone } = useUserStore();

  // 获取库存价格
  useProductStockPriceEffect(webData);

  const productRecord = webData?.productRecord || {};

  const brandVO = webData?.brandVO || {};

  const [idx, setIdx] = useState(0);

  const imgList = useMemo(() => {
    return (
      productRecord?.luceneBreviaryImageUrls?.split('<$>')?.map((item) => ({
        type: 'img',
        url: item,
      })) || []
    );
  }, [productRecord?.luceneBreviaryImageUrls]);

  const hasImage = useMemo(() => {
    return !!imgList.filter((item) => item?.url).length;
  }, [imgList]);

  const [svgData, setSvgData] = useState([]);

  const [svgDataFetched, setSvgDataFetched] = useState(false);

  const [svgList, setSvgList] = useState([]);

  const previewList = useMemo(() => {
    return [...imgList, ...svgList];
  }, [imgList, svgList]);

  const visibleThumbnails = useMemo(() => {
    const maxThumbnails = 5;
    let start = 0;
    let end = Math.min(maxThumbnails, previewList.length);

    if (previewList.length > maxThumbnails) {
      start = Math.max(0, idx - Math.floor(maxThumbnails / 2));
      start = Math.min(start, previewList.length - maxThumbnails);
      end = start + maxThumbnails;
    }

    return previewList.slice(start, end);
  }, [previewList, idx]);

  const { productModel, productGradePlateName, productType, productCode } = productRecord;

  const stockNumber =
    webData?.isOversea || productRecord?.selfProductCode
      ? productRecord?.stockNumber || 0
      : (webData?.gdWarehouseStockNumber || 0) + (webData?.jsWarehouseStockNumber || 0);

  const seo = useMemo(() => {
    return {
      title: `${productModel}_${productGradePlateName}-实物图-PCB引脚图-焊盘图-立创商城一站式电子元器件采购自营商城_嘉立创电子商城`,
      description: `立创商城提供 ${productGradePlateName} 的 ${productType} ${productModel} 的专业高清引脚图、PCB焊盘图、商品大图。正品保障，最快4小时发货。`,
      additionalMetaTags: [
        {
          name: 'keywords',
          content: `${productModel}，${productModel} 商品图片,${productModel} PDF手册,立创商城`,
        },
      ],
      additionalLinkTags: [
        {
          rel: 'canonical',
          href: pageUrl,
        },
      ],
      noindex: !hasImage,
    };
  }, [productModel, productGradePlateName, productType, pageUrl]);

  useEffect(() => {
    const { fromZone } = router.query;
    if (fromZone) {
      let track_zone = '';
      const zone = decodeURIComponent(decodeURIComponent(fromZone));
      if (zone) {
        track_zone += `__${zone}`;
      }
      updateFromZone(track_zone);
    }
    updateProjectConfigVO(resourceData?.projectConfigVO);
    getSvgData(productCode);
  }, []);

  // 加载EDA图
  const getSvgData = (productCode) => {
    sendAxios({
      method: 'GET',
      baseURL: 'https://lceda.cn/api/products/' + productCode + '/svgs',
    })
      .then((res) => {
        if (res.success) {
          const data = res.result || [];
          if (data.length) {
            setSvgData(data);
            setSvgList([
              { type: 'svg', name: 'sch' },
              { type: 'svg', name: 'pcb' },
            ]);
          }
        }
      })
      .finally(() => {
        setSvgDataFetched(true);
      });
  };

  const preImg = () => {
    if (idx !== 0) {
      setIdx(idx - 1);
    }
  };

  const nextImg = () => {
    if (idx < previewList.length - 1) {
      setIdx(idx + 1);
    }
  };

  const thumbnailList = useMemo(() => {
    return (
      <div className="shrink-0">
        <button
          aria-label="切换上一张图片"
          className="w-[14px] h-[14px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[224deg] hover:border-[#199FE9] cursor-pointer ml-[13px] mt-[10px]"
          onClick={preImg}
        ></button>
        <ul>
          {visibleThumbnails?.map((item) => {
            const actualIndex = previewList.findIndex((i) => i === item);
            return (
              <li key={actualIndex} className="mt-[15px]">
                {item.type === 'img' && (
                  <Image
                    className={`cursor-pointer rounded-[4px] w-[48px] h-[48px] ${actualIndex === idx ? 'border-solid border-[1px] border-[#199FE9]' : ''}`}
                    src={item.url}
                    alt={`${productRecord?.productModel}商品缩略图`}
                    height={48}
                    width={48}
                    fallback={defaultImg}
                    onClick={() => setIdx(actualIndex)}
                  />
                )}
                {item.type === 'svg' && (
                  <div
                    className={`flex items-center justify-center text-[12px] font-bold cursor-pointer rounded-[4px] w-[48px] h-[48px] border-solid border-[1px] border-[#e5e7eb] ${actualIndex === idx ? '!border-[#199FE9]' : ''}`}
                    onClick={() => setIdx(actualIndex)}
                  >
                    {item.name === 'sch' ? '引脚图' : '焊盘图'}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <button
          aria-label="切换下一张图片"
          className="w-[14px] h-[14px] border-solid border-[#B8BEC9] border-r-[2px] border-b-[2px] transform rotate-[43deg] hover:border-[#199FE9] cursor-pointer ml-[13px] mt-[16px]"
          onClick={nextImg}
        ></button>
      </div>
    );
  }, [previewList, idx, visibleThumbnails]);

  const DescriptionText = `本页面提供${brandVO?.brandName}的${productRecord?.productModel}产品的实物图、引脚图、原理图和PCB焊盘图。${productRecord?.productModel}参考售价为${price}元起，现货库存数量${stockNumber}个`;

  return (
    <>
      <NextSeo {...seo}></NextSeo>
      <div className="w-[100%] flex justify-center">
        <div>
          <LayoutProvider resourceData={resourceData}>
            <NavList data={webData} extraNavItem="商品大图" />
          </LayoutProvider>
          <div className="w-[1200px] flex mt-[16px]">
            <section className="rounded-[20px] px-[40px] py-[20px] bg-[#ffff]">
              <div className="flex">
                {visibleThumbnails.length ? thumbnailList : <></>}
                <div className="relative ml-[20px]">
                  <div className="mb-[16px] max-w-[630px] text-[14px] text-[#000000] truncate">
                    <span className="mr-[12px]" title={productRecord?.productModel}>
                      型号：{productRecord?.productModel}
                    </span>
                    <span className="mr-[12px]" title={brandVO?.brandName}>
                      品牌：{brandVO?.brandName}
                    </span>
                    <span title={productRecord?.productName}>名称：{productRecord?.productName}</span>
                  </div>
                  {(!svgList.length || previewList[idx]?.type === 'img') && (
                    <Image
                      className="size-[630px] mb-[13px] rounded-[8px]"
                      src={previewList[idx]?.url?.replace('product/breviary', 'product/source') || defaultImg}
                      title="商品大图"
                      alt={`${productRecord?.productModel}实物图`}
                      height={630}
                      width={630}
                      priority={true} // 预加载图片
                      fallback={defaultImg}
                    />
                  )}
                  <LcEdaCanvas
                    className={previewList[idx]?.name !== 'sch' ? 'hidden' : ''}
                    type="sch"
                    size={630}
                    data={svgData}
                    svgDataFetched={svgDataFetched}
                    productModel={productRecord?.productModel}
                  />
                  <LcEdaCanvas
                    className={previewList[idx]?.name !== 'pcb' ? 'hidden' : ''}
                    type="pcb"
                    size={630}
                    data={svgData}
                    svgDataFetched={svgDataFetched}
                    productModel={productRecord?.productModel}
                  />
                  <strong className="mt-[16px] block text-[14px] font-normal">{DescriptionText}</strong>
                  <div className="mt-[5px] text-[#69788A] text-[12px]">温馨提醒：图片仅供参考，商品以实物为准</div>
                </div>
              </div>
            </section>
            {/* 商品已下架 */}
            {webData?.productRecord?.isDisplay === false ? (
              <DynamicItemRightInquiry data={webData} />
            ) : (
              <DynamicItemRightPage data={webData} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  setContext(context);
  const { params } = context;
  const jpgUrl = params?.jpgUrl?.replace('.html', '');
  // 获取id
  const itemId = jpgUrl?.split('_')?.[1];

  if (!itemId) {
    return {
      notFound: true,
    };
  }

  const [webData, resourceData, notFound] = await asyncAll([
    ecpCurl(`${API.itemServe}${itemId}`),
    ecpCurl(StaticdataApi.resourceList, { enableCache: true }),
  ]);

  if (notFound) {
    return {
      notFound: true,
    };
  }

  const pageUrl = `${resourceData?.projectConfigVO?.lcscItemUrl}/product/${jpgUrl}.html`;

  const { productPriceList } = webData?.productRecord || {};

  const price = productPriceList?.length ? productPriceList[productPriceList.length - 1]?.thePrice : '';

  // 删除敏感数据,防止爬虫抓取
  delete webData?.productRecord?.productPriceList;
  delete webData?.productRecord?.productPriceDiscountList;
  delete webData?.productRecord?.entireProductPriceList;

  return {
    props: {
      webData,
      price,
      resourceData,
      pageUrl,
    },
  };
}
